import type { AppContext } from "../../app/app";
import { share } from "../../data/rating";
import {
  computeStanding,
  eventsSinceAnchor,
  indexInstances,
  standingKey,
  type Standing,
} from "../../data/standing";
import type {
  Instance,
  Person,
  PersonId,
  RatingEvent,
} from "../../data/model";
import { profileUrl } from "../../data/resolve";
import { purposesPresent } from "../../data/packs";
import { el } from "../dom";
import { renderDimRow, type LogEntry } from "../dim-row";
import { renderPurposeFilter } from "../dimension-tools";
import { renderPurposeChooser } from "../purpose";
import { renderPersonDomains } from "../domains";

// Person detail: edit identity, and for each quality see one evidence-updated
// score with a log of reads and events. No persistent 1..5 dial.
export async function renderPerson(
  ctx: AppContext,
  personId: PersonId,
): Promise<HTMLElement> {
  const [person, dimensions, allTags, allInstances, events, decisions] =
    await Promise.all([
      ctx.repo.getPerson(personId),
      ctx.repo.listDimensions(),
      ctx.repo.listTags({ viewer_id: ctx.viewer }),
      ctx.repo.listInstances({ viewer_id: ctx.viewer }),
      ctx.repo.listRatingEvents({ viewer_id: ctx.viewer, person_id: personId }),
      ctx.repo.listDecisions(),
    ]);

  const root = el("section", {});
  if (!person) {
    root.append(el("p", { class: "lede", text: "That person no longer exists." }));
    root.append(backLink(ctx));
    return root;
  }

  // Just-in-time fallback: arrived at a person with no qualities set yet. Lead to
  // a purpose before the rating UI, then continue once dimensions exist.
  if (dimensions.length === 0) {
    root.append(
      backLink(ctx),
      header(ctx, person, 0, 0),
      el("span", { class: "label", text: "Qualities" }),
      el("p", {
        class: "lede",
        text: "Pick what you are rating for, or use the generic set.",
      }),
      renderPurposeChooser(ctx, { onSeeded: () => ctx.refresh() }),
    );
    return root;
  }

  // Standing per tag (read nudged by events since that read); the per-dimension
  // share denominator is the sum of standing points across everyone.
  const now = Date.now();
  const instByPd = indexInstances(allInstances);
  const standingByTag = new Map<string, Standing>();
  const dimPointTotal = new Map<string, number>();
  for (const t of allTags) {
    const all = instByPd.get(standingKey(t.person_id, t.dimension_id)) ?? [];
    const since = eventsSinceAnchor(all, t.read_at ?? t.created_at);
    const st = computeStanding(t.rating, since, now);
    standingByTag.set(t.id, st);
    dimPointTotal.set(t.dimension_id, (dimPointTotal.get(t.dimension_id) ?? 0) + st.point);
  }

  // This person's reads and events, grouped by dimension, for the unified log.
  const readsByDim = new Map<string, RatingEvent[]>();
  for (const e of events) {
    const list = readsByDim.get(e.dimension_id) ?? [];
    list.push(e);
    readsByDim.set(e.dimension_id, list);
  }
  const eventsByDim = new Map<string, Instance[]>();
  for (const i of allInstances) {
    if (i.person_id !== personId) continue;
    const list = eventsByDim.get(i.dimension_id) ?? [];
    list.push(i);
    eventsByDim.set(i.dimension_id, list);
  }

  const ratedCount = allTags.filter((t) => t.person_id === personId).length;
  root.append(backLink(ctx), header(ctx, person, ratedCount, dimensions.length));

  const tagger = el("div", { class: "dim-tagger" });
  const rowByDim: { purposes: string[]; el: HTMLElement }[] = [];
  for (const dim of dimensions) {
    const mine = allTags.find((t) => t.person_id === personId && t.dimension_id === dim.id);
    const standing = mine ? standingByTag.get(mine.id)! : null;
    const total = dimPointTotal.get(dim.id) ?? 0;
    const myShare = mine && total > 0 ? share(standing!.point, total) : 0;
    const log = buildLog(readsByDim.get(dim.id) ?? [], eventsByDim.get(dim.id) ?? []);
    const row = renderDimRow(ctx, person, dim, mine, standing, myShare, log);
    rowByDim.push({ purposes: dim.purposes, el: row });
    tagger.append(row);
  }

  // Filter the quality rows by purpose so a large multi-purpose set stays
  // manageable. A row with no purposes shows only under "All".
  const filter = renderPurposeFilter(purposesPresent(dimensions), (key) => {
    for (const r of rowByDim) {
      r.el.style.display = key === null || r.purposes.includes(key) ? "" : "none";
    }
  });
  root.append(el("span", { class: "label", text: "Qualities" }));
  if (filter) root.append(filter);
  root.append(tagger);

  root.append(await renderPersonDomains(ctx, person.id));

  const referenced = decisions
    .filter((d) => (d.referenced_person_ids ?? []).includes(personId))
    .sort((a, b) => b.created_at - a.created_at);
  if (referenced.length > 0) {
    root.append(referencedDecisions(ctx, referenced));
  }
  return root;
}

// The Assemble briefs whose relevant set drew on this person. Each reopens the
// decision view with that prompt. No name matching: ids were stored at write
// time (see partner.assemble).
function referencedDecisions(
  ctx: AppContext,
  decisions: { id: string; prompt: string; created_at: number }[],
): HTMLElement {
  const wrap = el("div", { class: "person-decisions" });
  wrap.append(el("span", { class: "label", text: "Referenced in decisions" }));
  const list = el("div", { class: "recent__chips" });
  for (const d of decisions) {
    const short = d.prompt.length > 72 ? `${d.prompt.slice(0, 72)}...` : d.prompt;
    list.append(
      el("button", {
        class: "chip",
        text: short,
        title: d.prompt,
        on: { click: () => ctx.navigate({ name: "assemble", prompt: d.prompt }) },
      }),
    );
  }
  wrap.append(list);
  return wrap;
}

// Merge reads (with from -> to) and events into one log, newest first.
function buildLog(reads: RatingEvent[], events: Instance[]): LogEntry[] {
  const sortedReads = [...reads].sort((a, b) => a.at - b.at);
  let prev: number | null = null;
  const readEntries: LogEntry[] = sortedReads.map((r) => {
    const entry: LogEntry = {
      kind: "read",
      id: r.id,
      at: r.at,
      rating: r.rating,
      from: prev,
      note: r.rationale,
    };
    prev = r.rating;
    return entry;
  });
  const eventEntries: LogEntry[] = events.map((i) => ({
    kind: "event",
    id: i.id,
    at: i.occurred_at,
    valence: i.valence,
    magnitude: i.magnitude,
    note: i.note,
  }));
  return [...readEntries, ...eventEntries].sort((a, b) => b.at - a.at);
}

function backLink(ctx: AppContext): HTMLElement {
  return el("button", {
    class: "back",
    text: "← People",
    on: { click: () => ctx.navigate({ name: "people" }) },
  });
}

function header(
  ctx: AppContext,
  person: Person,
  rated: number,
  total: number,
): HTMLElement {
  const name = el("input", { class: "input input--title", value: person.display_name });
  const context = el("input", {
    class: "input",
    value: person.relationship_context,
    placeholder: "Relationship context (optional)",
  });
  const email = el("input", {
    class: "input",
    type: "email",
    value: person.email ?? "",
    placeholder: "Email (optional, kept on this device)",
  });
  const save = el("button", {
    class: "btn btn--ghost",
    text: "Save details",
    on: {
      click: async () => {
        await ctx.repo.updatePerson(person.id, {
          display_name: name.value.trim() || person.display_name,
          relationship_context: context.value.trim(),
          email: email.value.trim(),
        });
        ctx.refresh();
      },
    },
  });

  const link = profileUrl(person.target_key);
  const contact = el("div", { class: "person-head__contact" });
  if (link) {
    contact.append(
      el("a", { class: "person-head__link", href: link, text: "View profile", attrs: { target: "_blank", rel: "noopener noreferrer" } }),
    );
  }
  if (person.email) {
    contact.append(
      el("a", { class: "person-head__link", href: `mailto:${person.email}`, text: person.email }),
    );
  }
  const remove = el("button", {
    class: "btn btn--danger",
    text: "Delete",
    on: {
      click: async () => {
        await ctx.repo.deletePerson(person.id);
        ctx.navigate({ name: "people" });
      },
    },
  });
  return el(
    "div",
    { class: "person-head" },
    el("div", { class: "person-head__fields" }, name, context, email),
    contact.childElementCount ? contact : null,
    el(
      "div",
      { class: "person-head__row" },
      el("span", {
        class: "label provenance",
        text: `pointer ${person.target_key} · added ${person.source} · rated on ${rated}/${total} qualities`,
      }),
      el("span", { class: "person-head__actions" }, save, remove),
    ),
  );
}
